import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config';
import { siteInspections, siteUploads } from '../api';
import { FaSpinner, FaPlus, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

// ============================================
// INSPECTOR DIGITAL RIGGING SITE REPORT
// Public link - NO login required
// ============================================
const SAFETY_ITEMS = [
  'Red Seal Rigger', 'Standard Rigging Gear', 'Supervision', 'Method Statement',
  'Lifting Plan', 'Rigging Study', 'Risk Assessment', 'Safety File'
];

const WORKS_ITEMS = [
  'Rig Out', 'Load', 'Transport', 'Offload', 'Rig In', 'Position',
  'Uncrate', 'Ex Cont.', 'De-Comm', 'Comm'
];

function InspectorReport() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Report state
  const [report, setReport] = useState({
    assessor: '',
    date: new Date().toISOString().split('T')[0],
    surveyor_rate: '',
    scope: '',
    contact: '',
    telephone: '',
    email: '',
    moving_from: '',
    moving_from_contact: '',
    moving_from_tel: '',
    moving_to: '',
    moving_to_contact: '',
    moving_to_tel: '',
    assistant_riggers: ['', '', '', ''],
    safety: {},
    induction: '',
    rate_type: '',
    works: {},
    items: [{ qty: '', description: '', l: '', w: '', h: '', dia: '', weight: '' }],
    trucks: [{ truck_nr: '', qty: '', trailer: '', notes: '' }],
    load_days: '',
    transport_days: '',
    offload_days: '',
    permit: '',
    notes: ''
  });

  useEffect(() => {
    fetchInspection();
  }, [token]);

  const fetchInspection = async () => {
    setLoading(true);
    try {
      const data = await siteInspections.getByToken(token);
      if (!data.success) {
        setError(data.error || 'Invalid link');
        return;
      }
      if (data.expired) {
        setError('This report link has expired.');
        return;
      }
      if (data.inspection?.status === 'submitted') {
        setSuccess(true); // already done
        setInspection(data.inspection);
        return;
      }
      setInspection(data.inspection);
      const q = data.inspection?.quote || {};
      setReport(prev => ({
        ...prev,
        scope: q.description || q.commodity || '',
        moving_from: q.origin || '',
        moving_to: q.destination || '',
        contact: q.contact_person || '',
        email: q.contact_email || ''
      }));
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => setReport(prev => ({ ...prev, [key]: value }));

  const toggleCheck = (group, key) => {
    setReport(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: !prev[group]?.[key] }
    }));
  };

  const updateItem = (idx, field, value) => {
    const items = [...report.items];
    items[idx] = { ...items[idx], [field]: value };
    setField('items', items);
  };

  const addItem = () => setField('items', [...report.items, { qty: '', description: '', l: '', w: '', h: '', dia: '', weight: '' }]);
  const removeItem = (idx) => setField('items', report.items.filter((_, i) => i !== idx));

  const updateTruck = (idx, field, value) => {
    const trucks = [...report.trucks];
    trucks[idx] = { ...trucks[idx], [field]: value };
    setField('trucks', trucks);
  };

  const addTruck = () => setField('trucks', [...report.trucks, { truck_nr: '', qty: '', trailer: '', notes: '' }]);
  const removeTruck = (idx) => setField('trucks', report.trucks.filter((_, i) => i !== idx));

  const updateAssistantRigger = (idx, value) => {
    const riggers = [...report.assistant_riggers];
    riggers[idx] = value;
    setField('assistant_riggers', riggers);
  };

  const handleFilesSelected = (e) => {
    setFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Upload files to Supabase Storage
      const urls = [];
      setUploading(true);
      for (const file of files) {
        const res = await siteUploads.upload(file);
        if (res.success && res.url) urls.push(res.url);
      }
      setUploading(false);

      const response = await siteInspections.submitReportByToken(token, {
        inspector_notes: { ...report, assistant_riggers: report.assistant_riggers.filter(r => r) },
        file_urls: urls,
        assessor_name: report.assessor
      });

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.error || 'Failed to submit report');
      }
    } catch (err) {
      console.error('Submit report error:', err);
      setError('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaExclamationCircle className="text-5xl text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Link Invalid</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <a href="/" className="inline-block bg-navy text-white px-6 py-2 rounded-lg">Go to One Purpose Logistics</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Report Submitted!</h2>
          <p className="text-gray-500 mb-6">Your site inspection report has been submitted to One Purpose Logistics.</p>
          <a href="/" className="inline-block bg-green text-white px-6 py-2 rounded-lg">Done</a>
        </div>
      </div>
    );
  }

  const quote = inspection?.quote;
  const inputCls = "w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-green text-sm";

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-navy text-white rounded-t-xl p-6">
          <h1 className="text-2xl font-bold mb-1">🏗️ Rigging Site Report</h1>
          <p className="text-sm opacity-80">One Purpose Logistics — {quote?.quote_number || 'Site Inspection'}</p>
        </div>

        <div className="bg-white border border-gray-200 border-t-0 p-6">
          <form onSubmit={handleSubmit}>
            {/* Header fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Site Assessor *</label>
                <input className={inputCls} value={report.assessor} onChange={e => setField('assessor', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                <input type="date" className={inputCls} value={report.date} onChange={e => setField('date', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Surveyor Rate Estimate (R)</label>
                <input type="number" className={inputCls} value={report.surveyor_rate} onChange={e => setField('surveyor_rate', e.target.value)} placeholder="e.g. 3500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
                <input className={inputCls} value={report.scope} onChange={e => setField('scope', e.target.value)} readOnly={!!quote?.description} />
              </div>
            </div>

            {/* Customer */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
                <input className={inputCls} value={report.scope ? (quote?.customer_name || '') : ''} readOnly />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                <input className={inputCls} value={report.contact} onChange={e => setField('contact', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Telephone</label>
                <input className={inputCls} value={report.telephone} onChange={e => setField('telephone', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input className={inputCls} value={report.email} onChange={e => setField('email', e.target.value)} />
              </div>
            </div>

            {/* Moving from / to */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-navy mb-2">Moving From</h3>
                <textarea className={`${inputCls} mb-2`} rows="2" value={report.moving_from} onChange={e => setField('moving_from', e.target.value)} />
                <input className={`${inputCls} mb-2`} placeholder="Contact" value={report.moving_from_contact} onChange={e => setField('moving_from_contact', e.target.value)} />
                <input className={inputCls} placeholder="Tel" value={report.moving_from_tel} onChange={e => setField('moving_from_tel', e.target.value)} />
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-navy mb-2">Moving To</h3>
                <textarea className={`${inputCls} mb-2`} rows="2" value={report.moving_to} onChange={e => setField('moving_to', e.target.value)} />
                <input className={`${inputCls} mb-2`} placeholder="Contact" value={report.moving_to_contact} onChange={e => setField('moving_to_contact', e.target.value)} />
                <input className={inputCls} placeholder="Tel" value={report.moving_to_tel} onChange={e => setField('moving_to_tel', e.target.value)} />
              </div>
            </div>

            {/* Assistant riggers */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-navy mb-2">Assistant Riggers</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {report.assistant_riggers.map((r, idx) => (
                  <input key={idx} className={inputCls} placeholder={`Rigger ${idx + 1}`} value={r} onChange={e => updateAssistantRigger(idx, e.target.value)} />
                ))}
              </div>
            </div>

            {/* Safety file checklist */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-navy mb-2">Safety File / Documentation</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {SAFETY_ITEMS.map(item => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleCheck('safety', item)}
                    className={`p-2 rounded-lg border-2 text-sm text-left transition-all ${
                      report.safety[item] ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {report.safety[item] ? '✅ ' : '☐ '}{item}
                  </button>
                ))}
              </div>
            </div>

            {/* Special rigging induction + rate */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Special Rigging Induction</label>
                <select className={inputCls} value={report.induction} onChange={e => setField('induction', e.target.value)}>
                  <option value="">Select...</option>
                  <option value="Sameday">Sameday</option>
                  <option value="Before">Before</option>
                  <option value="None">None</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rate Type</label>
                <select className={inputCls} value={report.rate_type} onChange={e => setField('rate_type', e.target.value)}>
                  <option value="">Select...</option>
                  <option value="Day">Day</option>
                  <option value="Project">Project</option>
                </select>
              </div>
            </div>

            {/* Works checklist */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-navy mb-2">Works</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {WORKS_ITEMS.map(item => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => toggleCheck('works', item)}
                    className={`p-2 rounded-lg border-2 text-sm text-left transition-all ${
                      report.works[item] ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {report.works[item] ? '✅ ' : '☐ '}{item}
                  </button>
                ))}
              </div>
            </div>

            {/* Items table */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-navy mb-2">Items / Equipment</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-left text-xs">QTY</th>
                      <th className="px-2 py-2 text-left text-xs">Description</th>
                      <th className="px-2 py-2 text-left text-xs">L</th>
                      <th className="px-2 py-2 text-left text-xs">W</th>
                      <th className="px-2 py-2 text-left text-xs">H</th>
                      <th className="px-2 py-2 text-left text-xs">DIA</th>
                      <th className="px-2 py-2 text-left text-xs">WEIGHT</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item, idx) => (
                      <tr key={idx}>
                        <td><input className={`${inputCls} w-16`} value={item.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} /></td>
                        <td><input className={inputCls} value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-16`} value={item.l} onChange={e => updateItem(idx, 'l', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-16`} value={item.w} onChange={e => updateItem(idx, 'w', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-16`} value={item.h} onChange={e => updateItem(idx, 'h', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-16`} value={item.dia} onChange={e => updateItem(idx, 'dia', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-20`} value={item.weight} onChange={e => updateItem(idx, 'weight', e.target.value)} /></td>
                        <td><button type="button" onClick={() => removeItem(idx)} className="text-red-500"><FaTrash size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addItem} className="mt-2 text-sm text-green-600 flex items-center gap-1"><FaPlus size={12} /> Add Item</button>
            </div>

            {/* Trucks table */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-navy mb-2">Trucks / Trailers</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-left text-xs">Truck Nr</th>
                      <th className="px-2 py-2 text-left text-xs">QTY</th>
                      <th className="px-2 py-2 text-left text-xs">Trailer & Notes</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.trucks.map((truck, idx) => (
                      <tr key={idx}>
                        <td><input className={inputCls} value={truck.truck_nr} onChange={e => updateTruck(idx, 'truck_nr', e.target.value)} /></td>
                        <td><input className={`${inputCls} w-16`} value={truck.qty} onChange={e => updateTruck(idx, 'qty', e.target.value)} /></td>
                        <td><input className={inputCls} value={truck.trailer} onChange={e => updateTruck(idx, 'trailer', e.target.value)} /></td>
                        <td><button type="button" onClick={() => removeTruck(idx)} className="text-red-500"><FaTrash size={12} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addTruck} className="mt-2 text-sm text-green-600 flex items-center gap-1"><FaPlus size={12} /> Add Truck</button>
            </div>

            {/* Days + permit */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Load Days</label>
                <input type="number" className={inputCls} value={report.load_days} onChange={e => setField('load_days', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Transport Days</label>
                <input type="number" className={inputCls} value={report.transport_days} onChange={e => setField('transport_days', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Offload Days</label>
                <input type="number" className={inputCls} value={report.offload_days} onChange={e => setField('offload_days', e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Permit</label>
                <input className={inputCls} value={report.permit} onChange={e => setField('permit', e.target.value)} />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea className={inputCls} rows="4" value={report.notes} onChange={e => setField('notes', e.target.value)} />
            </div>

            {/* Uploads */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📎 Site Photos / Videos / Permit Documents <span className="text-xs text-gray-400">(photos + videos, up to 50MB)</span>
              </label>
              <div
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-green-500 transition-colors"
                onClick={() => document.getElementById('report-files').click()}
              >
                <p className="text-sm text-gray-600">Click to upload photos, videos, or documents</p>
                <input id="report-files" type="file" multiple accept="image/*,application/pdf,video/*" className="hidden" onChange={handleFilesSelected} />
              </div>
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 px-3">
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <button type="button" onClick={() => removeFile(idx)} className="text-red-500 text-sm">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">{error}</div>}

            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={submitting || uploading}
                className="w-full bg-green text-white py-3 rounded-lg font-semibold hover:bg-green transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {(submitting || uploading) && <FaSpinner className="animate-spin" />}
                {uploading ? 'Uploading files...' : submitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default InspectorReport;